// De deur. Eén veld, één knop, en daarna een mail.
//
// Ontwerpregel 1 van W8 geldt hier het scherpst: elke stand draagt zijn eigen
// reden in gewone taal. Er zijn er vier, en ze betekenen alle vier iets anders
// voor de bezoeker:
//
//  · vragen        — vul je adres in
//  · verstuurd     — kijk in je mail (geen belofte dat je erin mag; dat
//                    beslist de allowlist pas ná het klikken)
//  · geen_toegang  — je bent binnen bij Supabase, maar niet uitgenodigd. De
//                    service levert de Nederlandse reden; die tonen we letterlijk.
//  · niet_ingericht— deze omgeving heeft geen inlog (ontwikkelmodus).
//
// Wat hier NIET staat: een wachtwoord, een registratieknop en een "onthoud
// mij". Zelf aanmelden bestaat niet — een beheerder nodigt uit (W9).

import { useState, type FormEvent } from "react";

import { InlogFout, authIngericht, stuurMagicLink, uitloggen } from "../lib/auth";
import { color, font, radius, size, weight } from "../tokens";

type Stand =
  | { stand: "vragen" }
  | { stand: "versturen" }
  | { stand: "verstuurd"; email: string }
  | { stand: "mislukt"; reden: string };

export function InlogPagina({ geenToegang }: { geenToegang?: string }) {
  const [email, setEmail] = useState("");
  const [stand, setStand] = useState<Stand>({ stand: "vragen" });

  async function verstuur(e: FormEvent) {
    e.preventDefault();
    const adres = email.trim();
    if (!adres) return;
    setStand({ stand: "versturen" });
    try {
      await stuurMagicLink(adres);
      setStand({ stand: "verstuurd", email: adres.toLowerCase() });
    } catch (err) {
      setStand({
        stand: "mislukt",
        reden:
          err instanceof InlogFout || err instanceof Error
            ? err.message
            : "Versturen mislukt. Probeer het opnieuw.",
      });
    }
  }

  return (
    <Schil>
      {geenToegang ? (
        <GeenToegang reden={geenToegang} />
      ) : !authIngericht ? (
        <Blok
          titel="Geen inlog op deze omgeving"
          tekst={
            "Deze kopie draait zonder Supabase-sleutels, dus er is niets om op in te loggen. " +
            "Dat is de ontwikkelstand; op de echte omgeving staat hier een e-mailveld."
          }
        />
      ) : stand.stand === "verstuurd" ? (
        <Blok
          titel="Kijk in je mail"
          tekst={`We hebben een inloglink gestuurd naar ${stand.email}. De link is één keer te gebruiken en vervalt na een uur.`}
          onder={
            <Tekstknop
              onClick={() => {
                setStand({ stand: "vragen" });
              }}
            >
              Ander adres gebruiken
            </Tekstknop>
          }
        />
      ) : (
        <form onSubmit={verstuur}>
          <h1
            style={{
              fontSize: size.panelTitle,
              fontWeight: weight.bold,
              color: color.ink,
              letterSpacing: "-0.02em",
              marginBottom: 6,
            }}
          >
            Inloggen
          </h1>
          <p style={{ fontSize: size.body, color: color.muted, marginBottom: 20 }}>
            Vul je werkadres in. Je krijgt een link in je mail waarmee je binnenkomt — een
            wachtwoord is er niet.
          </p>

          <label
            htmlFor="inlog-email"
            style={{
              display: "block",
              fontSize: size.label,
              fontWeight: weight.medium,
              color: color.faint,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            E-mailadres
          </label>
          <input
            id="inlog-email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="naam@summacollege.nl"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              fontSize: size.body,
              fontFamily: "inherit",
              color: color.ink,
              background: color.surface,
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
              outline: "none",
            }}
          />

          {stand.stand === "mislukt" && (
            <p style={{ fontSize: size.meta, color: color.accentRed, marginTop: 10 }}>
              {stand.reden}
            </p>
          )}

          <button
            type="submit"
            disabled={stand.stand === "versturen"}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "11px 16px",
              fontSize: size.body,
              fontWeight: weight.medium,
              fontFamily: "inherit",
              color: color.surface,
              background: stand.stand === "versturen" ? color.disabled : color.ink,
              border: "none",
              borderRadius: radius.input,
              cursor: stand.stand === "versturen" ? "default" : "pointer",
            }}
          >
            {stand.stand === "versturen" ? "Versturen…" : "Stuur mij een inloglink"}
          </button>

          <p style={{ fontSize: size.meta, color: color.faint, marginTop: 14 }}>
            Alleen uitgenodigde adressen komen binnen. Sta je er nog niet bij, vraag dan een
            beheerder om een uitnodiging.
          </p>
        </form>
      )}
    </Schil>
  );
}

/** Je bent ingelogd bij Supabase, maar staat niet op de lijst — of je toegang
 *  is ingetrokken. De service schrijft de reden; wij verzinnen er geen. */
function GeenToegang({ reden }: { reden: string }) {
  return (
    <Blok
      titel="Geen toegang"
      tekst={reden}
      onder={
        <Tekstknop
          onClick={() => {
            void uitloggen();
          }}
        >
          Uitloggen en een ander adres proberen
        </Tekstknop>
      }
    />
  );
}

function Blok({ titel, tekst, onder }: { titel: string; tekst: string; onder?: React.ReactNode }) {
  return (
    <div>
      <h1
        style={{
          fontSize: size.panelTitle,
          fontWeight: weight.bold,
          color: color.ink,
          letterSpacing: "-0.02em",
          marginBottom: 8,
        }}
      >
        {titel}
      </h1>
      <p style={{ fontSize: size.body, color: color.muted, lineHeight: 1.55 }}>{tekst}</p>
      {onder && <div style={{ marginTop: 18 }}>{onder}</div>}
    </div>
  );
}

function Tekstknop({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: `1px solid ${color.border}`,
        color: color.body,
        borderRadius: radius.input,
        padding: "8px 14px",
        fontSize: size.meta,
        fontFamily: "inherit",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function Schil({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f5f5",
        fontFamily: font,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "min(420px, 100%)" }}>
        <div
          style={{
            fontSize: size.label,
            fontWeight: weight.medium,
            color: color.faint,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            marginBottom: 14,
          }}
        >
          Summa Trend Dashboard
        </div>
        <div
          style={{
            background: color.surface,
            border: `1px solid ${color.line}`,
            borderRadius: radius.card,
            padding: 28,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
